import { ReactElement, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import Title from "../../../components/title/Title";
import { getBillComplaintDetailsByRefNo } from "../../../services/fault";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import inProgressIcon from "../../../assets/images/icons/faultProgress/loading sc.gif";
import completedIcon from "../../../assets/images/icons/faultProgress/completed.svg";
import disabledIcon from "../../../assets/images/icons/faultProgress/disabled.svg";
import { IBillComplaintDetails } from "../../../interfaces/billComplaintDetails";
import styles from "./BillComplaintProgress.module.scss";
import useMediaQuery from "@mui/material/useMediaQuery";
import TextInfo from "../../../components/textInfo/TextInfo";
const BillComplaintProgress = () => {
  const { state } = useLocation();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [loading, setLoading] = useState<boolean>(false);
  const [billComplaintDetails, setBillComplaintDetails] =
    useState<IBillComplaintDetails>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [searchFaultId, setSearchFaultId] = useState<string>();
  const [forceSearch, setForceSearch] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (state) {
      setSearchFaultId((state as any).srNumber);
    }
  }, [state]);

  useEffect(() => {
    if (searchFaultId) {
      setBillComplaintDetails(undefined);
      const data = {
        faultReferenceNumber: searchFaultId.trim(),
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      setLoading(true);
      getBillComplaintDetailsByRefNo(data)
        .then((data: IBillComplaintDetails) => {
          setBillComplaintDetails(data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setLoading(false);
        });
    }
  }, [searchFaultId, forceSearch]);

  const detailsItem = (title: string, value: string | ReactElement | null) => (
    <>
      {value && (
        <Row>
          <Col className={[styles.detailsItem].join(" ")}>
            <p className={styles.title}>{title}</p>
            <div className={styles.value}>{value}</div>
          </Col>
        </Row>
      )}
    </>
  );

  const getShortAddress = (address: string | null): string => {
    if (!address) {
      return "";
    }
    const addressArray = address.split(",");
    return addressArray[addressArray?.length - 2];
  };

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    // setBillComplaintDetails(undefined);

    setSearchFaultId(value);
    setForceSearch((prev) => !prev);
  };

  interface Step {
    number: number;
    title: string;
    description?: string;
    active: boolean;
    icon?: JSX.Element;
  }

  const steps: Step[] = [
    {
      number: 1,
      title: "Reported",
      description: billComplaintDetails?.openedDate,
      active: !!billComplaintDetails?.status,
      icon: (
        <img
          className={styles.resizedGif}
          src={!!billComplaintDetails?.status ? completedIcon : disabledIcon}
          alt="Created"
        />
      ),
    },
    {
      number: 2,
      title: "Review By SLT",
      active: !!(
        billComplaintDetails?.status?.includes("Closed") ||
        billComplaintDetails?.subStatus?.includes("Assigned") ||
        billComplaintDetails?.status?.includes("Cancelled") ||
        billComplaintDetails?.subStatus?.includes("Resolved")
      ),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            billComplaintDetails?.status?.includes("Cancelled")
              ? completedIcon
              : billComplaintDetails?.subStatus?.includes("Assigned")
              ? inProgressIcon
              : billComplaintDetails?.subStatus?.includes("Resolved")
              ? completedIcon
              : disabledIcon
          }
          alt="Assigned"
        />
      ),
    },
    {
      number: 3,
      title: billComplaintDetails?.status?.includes("Cancelled")
        ? "Cancelled"
        : "Completed",
      active: !!(
        billComplaintDetails?.status?.includes("Closed") ||
        billComplaintDetails?.status?.includes("Cancelled") ||
        billComplaintDetails?.subStatus?.includes("Resolved")
      ),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            billComplaintDetails?.status?.includes("Closed") &&
            billComplaintDetails?.subStatus?.includes("Resolved")
              ? completedIcon
              : billComplaintDetails?.status?.includes("Cancelled")
              ? completedIcon
              : billComplaintDetails?.status?.includes("Open") &&
                billComplaintDetails?.subStatus?.includes("Resolved")
              ? inProgressIcon
              : disabledIcon
          }
          alt="Resolved"
        />
      ),
    },
  ];

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/tracker/status/billProgress",
            title: "Bill Complaint Status",
          },
        ]}
      />
      <Title>Bill Complaint Status</Title>
      <Row className="justify-content-center mt-2">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Bill Dispute Reference"
            onSearch={searchHandler}
            defaultValue={searchFaultId}
          />
        </Col>
      </Row>

      <Row>
        <Col className={[styles.container, "mt-4 py-5 px-5"].join(" ")}>
          {billComplaintDetails && (
            <Col>
              {detailsItem(
                "",
                <div className="bg-light rounded-3 p-3 mb-2 shadow-sm">
                  <Row className="align-items-center g-2">
                    {billComplaintDetails && (
                      <p
                        style={{
                          color: "#0A3D91",
                          fontWeight: 600,
                          fontSize: 16,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {" "}
                        Account Number :{" "}
                        {billComplaintDetails?.sltBillingAccountNumber}
                      </p>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          )}
          {billComplaintDetails ? (
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={4}
              sx={{
                flexWrap: "wrap",
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width={isMobile ? "100%" : "260px"}
                  height={isMobile ? "auto" : "110px"}
                  borderRadius="15px"
                  padding="10px 20px"
                  sx={{
                    background: step.active
                      ? "linear-gradient(to right, #D4EAF7, #F2F8FF)"
                      : "#E5E5E5",
                    color: step.active ? "#0A3D91" : "#A0A0A0",
                    boxShadow: step.active ? 3 : 0,
                    transition: "0.3s",
                  }}
                >
                  <Box
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    m={isMobile ? 1 : 1}
                    mr={isMobile ? 2 : 1}
                    sx={{
                      backgroundColor: step.active ? "#51B848" : "#BDBDBD",
                      color: "white",
                      fontSize:
                        step.number === 2 || step.number === 3
                          ? "16px"
                          : "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Box
                    width="3px"
                    height="35px"
                    mx={2}
                    sx={{
                      backgroundColor: step.active ? "#0A3D91" : "#A0A0A0",
                    }}
                  />

                  <Box>
                    <Typography
                      fontWeight={step.active ? "bold" : "normal"}
                      fontSize={isMobile ? "12px" : "14px"}
                      sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        fontSize={isMobile ? "10px" : "12px"}
                        sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {billComplaintDetails?.status?.includes("Cancelled") && (
                <Row className="mt-3 justify-content-center">
                  <Col className="col-md-12">
                    <TextInfo color="primary">
                      <b>
                        <i
                          className="poppins-regular"
                          style={{ fontStyle: "italic" }}
                        >
                          Note:Your Bill complaint has been cancelled.Please
                          contact your Account Manager for further details.
                        </i>
                      </b>
                    </TextInfo>
                  </Col>
                </Row>
              )}
            </Box>
          ) : (
            <EmptyPlaceholder />
          )}
        </Col>
      </Row>

      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            minWidth: "56px",
            width: "300px",
            height: "50px",
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
          }}
          onClick={() => {
            navigate(`/tracker/status/billProgress/ongoing`);
          }}
        >
          View All Ongoing Bill Complaints
        </Button>
      </Box>

      {/* error message */}
      <AlertModal
        show={!!errorMessage}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: () => {
              setErrorMessage(undefined);
            },
          },
        ]}
        variant={"error"}
      >
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default BillComplaintProgress;
